import React, { useState, useEffect, useCallback } from "react";
import { Mail, AlertTriangle, RefreshCw, LogOut, Layers } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";

const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
  </svg>
);

const EmailConfig = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [emailConfig, setEmailConfig] = useState({
    sender_email: "",
    sender_name: "",
    is_active: false,
    google_connected: false,
  });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ApiClient.request("/email/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setEmailConfig({
          sender_email: response.config.sender_email || "",
          sender_name: response.config.sender_name || "",
          is_active: response.config.is_active || false,
          google_connected: response.config.google_connected || false,
        });
      }
    } catch (error) {
      console.log("Email config not found");
    } finally {
      setLoading(false);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const toggleActive = async () => {
    if (!emailConfig.google_connected) {
      showToast("Please connect your Google Account first.", "error");
      return;
    }

    try {
      const newActiveState = !emailConfig.is_active;
      const response = await ApiClient.request("/email/config", { aiAgentId: activeAiAgentId, 
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setEmailConfig((prev) => ({ ...prev, is_active: newActiveState }));
        showToast(
          newActiveState ? "Email Agent is now Operational" : "Email Agent is now Offline",
          "success"
        );
      }
    } catch (error) {
      // Error handled by ApiClient
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const response = await ApiClient.request("/email/oauth/url", { aiAgentId: activeAiAgentId });
      if (response && response.url) {
        window.location.href = response.url;
      } else {
        showToast("Failed to retrieve Google authorization URL", "error");
      }
    } catch (error) {
      console.error("OAuth error:", error);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      const response = await ApiClient.request("/email/oauth/disconnect", { aiAgentId: activeAiAgentId, 
        method: "POST",
      });
      if (response.success) {
        showToast("Disconnected Google Account successfully.", "success");
        setEmailConfig({
          sender_email: "",
          sender_name: "",
          is_active: false,
          google_connected: false,
        });
      }
    } catch (error) {
      console.error("Disconnect error:", error);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100">
      <div className="w-full max-w-4xl space-y-8">
        <PageHeader
          title="Email Integration"
          description="Supercharge your AI Agent with direct email capabilities. Connect your Google account to let the agent process and reply to support emails autonomously."
          actions={
            <div className="flex items-center gap-3 bg-white px-5 py-3 rounded-[24px] border border-gray-100 shadow-xl shadow-gray-100/50">
              <span
                className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 ${
                  emailConfig.is_active && emailConfig.google_connected
                    ? "text-[#83bbe1]"
                    : "text-gray-300"
                }`}
              >
                {emailConfig.is_active && emailConfig.google_connected
                  ? "Operational"
                  : "offline"}
              </span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailConfig.is_active && emailConfig.google_connected}
                  onChange={toggleActive}
                  disabled={!emailConfig.google_connected}
                  className="sr-only peer"
                />
                <div
                  className={`w-11 h-6 bg-white border-2 rounded-full peer transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 ${
                    emailConfig.google_connected
                      ? "border-gray-200 peer-checked:border-[#83bbe1] peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1] after:bg-gray-300 cursor-pointer"
                      : "border-gray-100 after:bg-gray-200 opacity-40 cursor-not-allowed"
                  }`}
                ></div>
              </label>
            </div>
          }
        />

        <div className="grid gap-8 animate-slideIn">
          <div className="bg-white rounded-[32px] p-8 md:p-12 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
            {/* Background design elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-radial-gradient from-blue-50/20 to-transparent -mr-16 -mt-16 rounded-full pointer-events-none" />

            {!emailConfig.google_connected ? (
              <div className="flex flex-col items-center text-center space-y-8 py-6">
                {/* Sign in with Google Button where the Mail Icon was */}
                <div className="relative w-full max-w-sm z-10">
                  <div className="absolute inset-0 bg-[#83bbe1]/20 blur-xl rounded-full scale-150 animate-pulse-slow pointer-events-none"></div>
                  <button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="relative w-full flex items-center justify-center px-6 py-4 bg-white border border-gray-200 text-gray-700 rounded-2xl hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-bold text-sm shadow-md shadow-gray-100 active:scale-[0.98]"
                  >
                    {connecting ? (
                      <RefreshCw size={18} className="animate-spin text-gray-500 mr-2" />
                    ) : (
                      <GoogleIcon />
                    )}
                    Sign in with Google
                  </button>
                </div>

                <div className="max-w-md space-y-2">
                  <h3 className="text-xl font-bold text-gray-900">Connect Google Account</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    Link your Gmail inbox to enable automated customer support. The AI agent will monitor your emails and reply to customer inquiries instantly.
                  </p>
                </div>

                <div className="w-full max-w-sm">
                  <p className="text-[10px] text-gray-400 leading-normal">
                    By connecting, you authorize EasyChat to read, send, and manage your emails. You can disconnect at any time.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Connected status layout */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center text-green-500 border border-green-100 shadow-sm">
                        <Mail size={24} />
                      </div>
                      <span className="absolute -bottom-1 -right-1 flex h-4.5 w-4.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-4.5 w-4.5 bg-green-500 border-2 border-white"></span>
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold uppercase tracking-wider rounded-md border border-green-100">
                          Connected
                        </span>
                        {emailConfig.is_active && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold uppercase tracking-wider rounded-md border border-blue-100">
                            Active
                          </span>
                        )}
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mt-1">{emailConfig.sender_email}</h4>
                      <p className="text-xs text-gray-400 font-medium">
                        Display Name: <span className="font-semibold text-gray-600">{emailConfig.sender_name || "EasyChat Agent"}</span>
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="flex items-center justify-center gap-2 px-5 py-3 border border-red-100 text-red-600 bg-red-50/30 hover:bg-red-50 rounded-xl transition-all duration-300 font-bold text-xs active:scale-[0.98] disabled:opacity-50"
                  >
                    {disconnecting ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : (
                      <LogOut size={14} />
                    )}
                    Disconnect Account
                  </button>
                </div>

                {/* Operational Details */}
                <div className="space-y-6">
                  <div className="p-6 bg-gray-50/50 rounded-2xl border border-gray-100 space-y-4">
                    <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-widest">
                      <Layers size={14} className="text-gray-500" />
                      Integration Details
                    </div>
                    
                    <ul className="space-y-3 text-xs text-gray-600">
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 bg-[#83bbe1] rounded-full mt-1.5 shrink-0" />
                        <span><strong>Direct Google Connection:</strong> Authenticates autonomously using secure OAuth 2.0 without requiring app passkeys.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 bg-[#83bbe1] rounded-full mt-1.5 shrink-0" />
                        <span><strong>Real-Time Inbox Scanning:</strong> Automatically monitors your connected Gmail account for incoming support emails.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 bg-[#83bbe1] rounded-full mt-1.5 shrink-0" />
                        <span><strong>AI-Powered Drafts & Replies:</strong> Drafts context-aware replies matching your agent's knowledge and settings.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 bg-[#83bbe1] rounded-full mt-1.5 shrink-0" />
                        <span><strong>Background Processing:</strong> Runs continuously on the server even when the agent dashboard is closed.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 bg-[#83bbe1] rounded-full mt-1.5 shrink-0" />
                        <span><strong>Automatic Token Refresh:</strong> Securely handles access token rotation behind the scenes to maintain active status.</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <span className="w-1.5 h-1.5 bg-[#83bbe1] rounded-full mt-1.5 shrink-0" />
                        <span><strong>Granular Authorization:</strong> Restricts operations only to those authorized by your Google OAuth consent screen.</span>
                      </li>
                    </ul>
                  </div>

                  {!emailConfig.is_active && (
                    <div className="flex items-start gap-3 p-4 bg-amber-50/50 rounded-2xl border border-amber-100/50">
                      <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-800 leading-relaxed">
                        The account is connected but the integration is currently <span className="font-bold">Offline</span>. Toggle the switch in the top-right corner to start processing emails.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfig;
